import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Skeleton,
} from "@mui/material";
import axios from "axios";
import {
  FormLabel,
  ModalError,
  ModalSuccess,
  MultipleSelectInputWithTags,
  PasswordInput,
  NumberInput,
  PrimaryButton,
  SecondaryButton,
  TextInput,
} from "components";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { hardBaseUrl } from "services/urlConstant";
import {
  arrayToCommaSeparatedString,
  getErrors,
  stringTruncate,
  validateEmail,
  validatePassword,
} from "utils";

const ModalFormParameterUser = ({
  open,
  onClose,
  preload,
  userLogin,
  getDataTable,
}) => {
  const typeWeb = localStorage.getItem("typeWeb");
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const isMpis = typeWeb === "mpis";
  const urlEndpoints = `${hardBaseUrl}/${isMpis ? "" : "publisher/"}user${
    preload ? `/${preload}` : ""
  }`;
  const roleAdmin = userLogin?.role?.type === "admin";
  const publisherId = useSelector(state => state.publisherId);
  const [loadingPage, setLoadingPage] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [optionRole, setOptionRole] = useState([]);
  const [filteredOptionRole, setFilteredOptionRole] = useState([]);
  const [searchRole, setSearchRole] = useState("");
  const [payload, setPayload] = useState({
    name: "",
    email: "",
    phone: "",
    role_ids: [],
    ...(!preload && { password: "", re_password: "" }),
  });
  const [passwordError, setPasswordError] = useState(null);
  const [emailError, setEmailError] = useState(null);

  const handleChangePayload = (value, key) => {
    setPayload(currentState => ({
      ...currentState,
      [key]: value,
    }));
    if (!preload && (key === "password" || key === "confirm_password")) {
      setPasswordError(null);
    }
    if (key === "email") {
      setEmailError(null);
    }
  };
  const handleSelectRole = (event, option) => {
    const { checked } = event?.target || false;
    setPayload(prev => ({
      ...prev,
      role_ids: checked
        ? [...prev?.role_ids, option]
        : prev?.role_ids?.filter(item => item?.role_id !== option?.role_id),
    }));
  };

  const getOptionRole = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(`${hardBaseUrl}/role`, {
        headers,
        params: {
          type: roleAdmin ? "admin" : "publisher",
        },
      });
      const { data } = res?.data;
      setOptionRole(data || []);
      setFilteredOptionRole(data || []);
    } catch (error) {
      ModalError(error?.response ? getErrors(error?.response) : error);
    } finally {
      if (!preload) setLoadingPage(false);
    }
  };
  const getDetail = async () => {
    try {
      const res = await axios.get(urlEndpoints, {
        headers,
      });
      const { name, email, phone, role_ids } = res?.data?.data;
      const preload = {
        name,
        email,
        phone,
        role_ids: role_ids.map(
          roleId => optionRole.find(option => option.role_id === roleId) || {}
        ),
      };
      setPayload(preload);
    } catch (error) {
      ModalError(error?.response ? getErrors(error?.response) : error);
    } finally {
      setLoadingPage(false);
    }
  };
  const handleSubmit = async () => {
    const method = preload ? axios.put : axios.post;
    const { password, confirm_password, role_ids, ...restPayload } = payload;
    const modifiedPayload = {
      ...restPayload,
      role_ids: role_ids?.map(item => item?.role_id),
      ...(!preload && { publisher_id: publisherId.publisherId, password }),
    };
    const validationError = validatePassword({
      password,
      confirmPassword: confirm_password,
      minLength: 8,
    });
    let hasError = false;
    setPasswordError(null);
    setEmailError(null);

    if (validationError) {
      setPasswordError(validationError);
      hasError = true;
    }

    if (!validateEmail(payload?.email)) {
      setEmailError(
        "Invalid email, please enter your email address in format: yourname@example.com"
      );
      hasError = true;
    }

    if (hasError) return;

    try {
      setLoadingButton(true);
      await method(urlEndpoints, modifiedPayload, {
        headers,
      });
      ModalSuccess(
        `Successfully ${preload ? "update" : "save"} user`
      ).then(() => getDataTable());
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingButton(false);
      onClose();
    }
  };

  useEffect(() => {
    getOptionRole();
  }, []);
  useEffect(() => {
    if (optionRole?.length > 0 && preload) getDetail();
  }, [optionRole]);
  useEffect(() => {
    setFilteredOptionRole(
      searchRole
        ? optionRole?.filter(role =>
            role?.name?.toLowerCase().includes(searchRole?.toLowerCase())
          )
        : optionRole
    );
  }, [searchRole]);

  return (
    <Dialog fullWidth open={open} onClose={onClose}>
      <DialogTitle>{`${preload ? "Edit" : "Add"} User`}</DialogTitle>
      <DialogContent dividers>
        {loadingPage ? (
          <Grid container direction="column" rowSpacing={2}>
            {Object.keys(payload).map((_, index) => (
              <Grid item key={index}>
                <Box mb="8px">
                  <Skeleton variant="rounded" width="40%" height={25} />
                </Box>
                <Skeleton variant="rounded" width="100%" height={30} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container direction="column" rowSpacing={1}>
            {MockFieldForm({ preload, passwordError, emailError })?.map(
              ({ title, key, type, error, helperText }, index) => (
                <Grid item key={index}>
                  <FormLabel label={title} required />
                  {type === "select" ? (
                    <MultipleSelectInputWithTags
                      options={filteredOptionRole}
                      placeholder="Choose Role"
                      value={payload?.role_ids}
                      textValue={stringTruncate(
                        arrayToCommaSeparatedString(payload?.role_ids, "name"),
                        40
                      )}
                      optionKey="role_id"
                      optionLabel="name"
                      onChange={handleSelectRole}
                      search
                      searchValue={searchRole}
                      onChangeSearch={setSearchRole}
                      customPaperWidth={552}
                      disabled={loadingButton}
                    />
                  ) : type === "password" ? (
                    <PasswordInput
                      placeholder={title}
                      autoComplete={`new-${key}`}
                      value={payload?.[key]}
                      onChange={e => handleChangePayload(e?.target?.value, key)}
                      error={!!error}
                      helperText={error || helperText}
                      disabled={loadingButton}
                    />
                  ) : type === "phone" ? (
                    <NumberInput
                      placeholder={title}
                      autoComplete={`new-${key}`}
                      value={payload?.[key]}
                      onChange={e => handleChangePayload(e, key)}
                      disabled={loadingButton}
                    />
                  ) : (
                    <TextInput
                      placeholder={title}
                      value={payload?.[key]}
                      autoComplete={`new-${key}`}
                      onChange={e => handleChangePayload(e?.target?.value, key)}
                      error={!!error}
                      helperText={error}
                      disabled={loadingButton}
                    />
                  )}
                </Grid>
              )
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <SecondaryButton
          label="Cancel"
          disabled={loadingPage}
          onClick={onClose}
        />
        <PrimaryButton
          label={loadingButton ? "Saving" : "Save"}
          loading={loadingButton}
          disabled={loadingPage || loadingButton}
          onClick={handleSubmit}
        />
      </DialogActions>
    </Dialog>
  );
};

const MockFieldForm = ({ preload, passwordError, emailError }) => [
  {
    title: "Full Name",
    key: "name",
  },
  {
    title: "Role Name",
    key: "role_ids",
    type: "select",
  },
  {
    title: "Email",
    key: "email",
    error: emailError,
  },
  {
    title: "Phone Number",
    key: "phone",
    type: "phone",
  },
  ...(preload
    ? []
    : [
        {
          title: "Password",
          key: "password",
          type: "password",
          helperText: "Minimum 8 Characters",
          error: passwordError,
        },
        {
          title: "Confirm Password",
          key: "confirm_password",
          type: "password",
          error: passwordError,
        },
      ]),
];
export default ModalFormParameterUser;
